import React, { useState, useRef, useEffect } from 'react';
import { Form, Input, Select, Switch, Row, Col, Space, Button, Table, Tag, Upload, message, Popconfirm } from 'antd';
import { Bank } from '../../services/banks';
import { CustomerRequest, CustomerContactRequest, CustomerFinancialRequest, CustomerReportingRequest } from '../../services/customers';
import { UploadOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import api from '../../services/api';
import customerApi from '../../services/customerApi';

const sexOptions = [
  { label: 'Male', value: 'Male' },
  { label: 'Female', value: 'Female' },
];

const assessmentOptions = [
  { label: 'B', value: 'B' },
  { label: 'BE', value: 'BE' },
];

interface OverviewTabProps {
  form: any;
  isEdit: boolean;
  isEditing: boolean;
  canEditDetails?: boolean;
  canVerify?: boolean;
  canViewSignature?: boolean;
  canEditSignature?: boolean;
  canDeleteSignature?: boolean;
  banks: Bank[];
  getDisplayValue: (fieldName: string) => string;
  handleFieldChange: (fieldName: string, value: string) => void;
  getChangeRequestForField: (fieldName: string) => any;
  handleApproveChangeRequest: (requestId: number) => void;
  handleRejectChangeRequest: (requestId: number) => void;
  handleApprovePendingChange: (fieldName: string) => void;
  handleRejectPendingChange: (fieldName: string) => void;
  isCustomerPortal?: boolean;
  reportingFiles?: {
    identification_passport?: UploadFile[];
    bank?: UploadFile[];
    address?: UploadFile[];
    signature?: UploadFile[];
  };
  onFileChange?: (field: string, files: UploadFile[]) => void;
  customerId?: number;
  latestRequests?: {
    customer?: CustomerRequest | null;
    contact?: CustomerContactRequest | null;
    financial?: CustomerFinancialRequest | null;
    reporting?: CustomerReportingRequest | null;
  };
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  form,
  isEdit,
  isEditing,
  canEditDetails = true,
  canVerify = false,
  canViewSignature = true,
  canEditSignature = true,
  canDeleteSignature = true,
  banks,
  getDisplayValue,
  handleFieldChange,
  getChangeRequestForField,
  handleApproveChangeRequest,
  handleRejectChangeRequest,
  handleApprovePendingChange,
  handleRejectPendingChange,
  isCustomerPortal = false,
  reportingFiles = {},
  onFileChange,
  customerId,
  latestRequests = {},
}) => {
  const canEditDetailsFields = isEditing && canEditDetails;
  const canEditSignatureFields = isEditing && canEditSignature;
  const canDeleteSignatureField = isEditing && canEditSignature;
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(null);
  const [requestSignaturePreviewUrl, setRequestSignaturePreviewUrl] = useState<string | null>(null);
  const [fileActionLoading, setFileActionLoading] = useState<Record<string, { view?: boolean; download?: boolean }>>({});
  const hasSignature = (reportingFiles.signature || []).length > 0;

  const setFileLoading = (field: string, action: 'view' | 'download', isLoading: boolean) => {
    setFileActionLoading((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [action]: isLoading,
      },
    }));
  };

  // Load signature preview when file is available
  useEffect(() => {
    let blobUrlToRevoke: string | null = null;
    let isCancelled = false;
    
    const signatureFile = reportingFiles.signature?.[0];
    
    // Clean up previous preview URL
    setSignaturePreviewUrl((prevUrl) => {
      if (prevUrl) {
        URL.revokeObjectURL(prevUrl);
      }
      return null;
    });
    
    if (signatureFile) {
      if (signatureFile.originFileObj) {
        // New file - create blob URL from originFileObj
        const blobUrl = URL.createObjectURL(signatureFile.originFileObj);
        if (!isCancelled) {
          setSignaturePreviewUrl(blobUrl);
          blobUrlToRevoke = blobUrl;
        } else {
          URL.revokeObjectURL(blobUrl);
        }
      } else if (signatureFile.url && customerId) {
        // Existing file from S3 - fetch via API and create blob URL
        // Always fetch via API to ensure blob URL is used
        const loadPreview = async () => {
          try {
            const isCustomerPortalPath = window.location.pathname === '/customer'
              || window.location.pathname.startsWith('/customer/');
            const url = isCustomerPortalPath
              ? `/customer/supporting-files/${customerId}/signature/view`
              : `/customers/${customerId}/supporting-files/signature/view`;
            const apiClient = isCustomerPortalPath ? customerApi : api;
            
            const response = await apiClient.get(url, {
              responseType: 'blob',
            });
            
            if (isCancelled) {
              return;
            }
            
            const blob = new Blob([response.data], { 
              type: response.headers['content-type'] || 'image/png' 
            });
            const blobUrl = URL.createObjectURL(blob);
            setSignaturePreviewUrl(blobUrl);
            blobUrlToRevoke = blobUrl;
          } catch (error) {
            if (!isCancelled) {
              // Failed to load preview
              setSignaturePreviewUrl(null);
            }
          }
        };
        loadPreview();
      } else {
        setSignaturePreviewUrl(null);
      }
    } else {
      setSignaturePreviewUrl(null);
    }
    
    return () => {
      isCancelled = true;
      if (blobUrlToRevoke) {
        URL.revokeObjectURL(blobUrlToRevoke);
      }
      // Also revoke current preview URL if it exists
      setSignaturePreviewUrl((currentUrl) => {
        if (currentUrl && currentUrl.startsWith('blob:')) {
          URL.revokeObjectURL(currentUrl);
        }
        return null;
      });
    };
  }, [reportingFiles.signature, customerId]);

  useEffect(() => {
    let blobUrlToRevoke: string | null = null;
    let isCancelled = false;
    const requestedSignature = getRequestedFileValue('signature');

    setRequestSignaturePreviewUrl((prevUrl) => {
      if (prevUrl) {
        URL.revokeObjectURL(prevUrl);
      }
      return null;
    });

    if (!requestedSignature || !customerId) {
      return () => {
        isCancelled = true;
        if (blobUrlToRevoke) {
          URL.revokeObjectURL(blobUrlToRevoke);
        }
      };
    }

    const loadRequestedPreview = async () => {
      try {
        const response = await api.get(`/customer-requests/${customerId}/reporting/signature/view`, {
          responseType: 'blob',
        });
        if (isCancelled) return;
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'image/png',
        });
        const blobUrl = URL.createObjectURL(blob);
        setRequestSignaturePreviewUrl(blobUrl);
        blobUrlToRevoke = blobUrl;
      } catch {
        if (!isCancelled) {
          setRequestSignaturePreviewUrl(null);
        }
      }
    };

    loadRequestedPreview();

    return () => {
      isCancelled = true;
      if (blobUrlToRevoke) {
        URL.revokeObjectURL(blobUrlToRevoke);
      }
      setRequestSignaturePreviewUrl((currentUrl) => {
        if (currentUrl && currentUrl.startsWith('blob:')) {
          URL.revokeObjectURL(currentUrl);
        }
        return null;
      });
    };
  }, [customerId, latestRequests.reporting?.signature]);

  const handleView = async (field: string, file: UploadFile) => {
    if (!file) return;
    
    try {
      setFileLoading(field, 'view', true);
      // If it's a new file (has originFileObj), use it directly
      if (file.originFileObj) {
        const blobUrl = URL.createObjectURL(file.originFileObj);
        const newWindow = window.open(blobUrl, '_blank');
        
        // Clean up blob URL after window is closed or after a delay
        if (newWindow) {
          newWindow.addEventListener('beforeunload', () => {
            URL.revokeObjectURL(blobUrl);
          });
        }
        setTimeout(() => {
          try {
            URL.revokeObjectURL(blobUrl);
          } catch (e) {
            // Ignore errors if URL was already revoked
          }
        }, 60000); // 60 seconds
      } else {
        // For existing files from S3, fetch via API
        if (!customerId) return;
        
        const isCustomerPortalPath = window.location.pathname === '/customer'
          || window.location.pathname.startsWith('/customer/');
        let url: string;
        let apiClient: any;
        
        if (isCustomerPortalPath) {
          // Customer portal route: /customer/supporting-files/{id}/{field}/view
          url = `/customer/supporting-files/${customerId}/${field}/view`;
          apiClient = customerApi;
        } else {
          // Admin portal route: /customers/{id}/supporting-files/{field}/view
          url = `/customers/${customerId}/supporting-files/${field}/view`;
          apiClient = api;
        }
        
        // Use axios to fetch file with authentication token (similar to Files module)
        const response = await apiClient.get(url, {
          responseType: 'blob',
        });
        
        // Create blob URL and open in new window
        const blob = new Blob([response.data], { 
          type: response.headers['content-type'] || 'application/octet-stream' 
        });
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Open blob URL in new window
        const newWindow = window.open(blobUrl, '_blank');
        
        // Clean up blob URL after window is closed or after a delay
        if (newWindow) {
          newWindow.addEventListener('beforeunload', () => {
            window.URL.revokeObjectURL(blobUrl);
          });
        }
        // Also clean up after a longer delay as fallback
        setTimeout(() => {
          try {
            window.URL.revokeObjectURL(blobUrl);
          } catch (e) {
            // Ignore errors if URL was already revoked
          }
        }, 60000); // 60 seconds
      }
    } catch (error: any) {
      message.error('Failed to view file');
    } finally {
      setFileLoading(field, 'view', false);
    }
  };

  const handleViewRequested = async (field: string) => {
    if (!customerId) return;
    try {
      setFileLoading(field, 'view', true);
      const url = `/customer-requests/${customerId}/reporting/${field}/view`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      message.error('Failed to view requested file');
    } finally {
      setFileLoading(field, 'view', false);
    }
  };

  const getCurrentValue = (fieldName: string): string => {
    return form.getFieldValue(fieldName) || '-';
  };

  const normalizeValue = (value: any): string => {
    if (value === undefined || value === null) return '';
    return String(value);
  };

  const getRequestedValue = (fieldName: string): string | null => {
    const customerRequest = latestRequests.customer;
    const contactRequest = latestRequests.contact;
    const financialRequest = latestRequests.financial;
    const reportingRequest = latestRequests.reporting;

    if (fieldName === 'contact_number') {
      return contactRequest?.contact_number ?? null;
    }
    if (fieldName === 'address1') {
      return contactRequest?.address1 ?? null;
    }
    if (fieldName === 'address2') {
      return contactRequest?.address2 ?? null;
    }
    if (fieldName === 'bank_number') {
      return financialRequest?.bank_number ?? null;
    }
    if (fieldName === 'bank') {
      return reportingRequest?.bank_text ?? reportingRequest?.bank ?? null;
    }
    if (fieldName === 'email') {
      return customerRequest?.email ?? contactRequest?.email ?? null;
    }

    if (!customerRequest) {
      return null;
    }
    return (customerRequest as any)[fieldName] ?? null;
  };

  const getRequestedFileValue = (fieldName: string): string | null => {
    const reportingRequest = latestRequests.reporting;
    if (!reportingRequest) return null;
    return (reportingRequest as any)[fieldName] ?? null;
  };

  const getPendingValue = (fieldName: string): string | null => {
    const changeRequest = getChangeRequestForField(fieldName);
    if (changeRequest && changeRequest.status === 'pending') {
      return changeRequest.new_value;
    }
    const displayValue = getDisplayValue(fieldName);
    return displayValue || null;
  };

  const getOldValue = (fieldName: string): string | null => {
    const changeRequest = getChangeRequestForField(fieldName);
    if (changeRequest && changeRequest.status === 'pending') {
      return changeRequest.old_value || null;
    }
    return null;
  };

  const hasPendingChange = (fieldName: string): boolean => {
    return getPendingValue(fieldName) !== null;
  };

  const renderFileUploadButtons = (fileField: string) => {
    const files = reportingFiles[fileField as keyof typeof reportingFiles];
    const hasFile = files && files.length > 0;
    const hasRequestedFile = !!getRequestedFileValue(fileField);

    return (
      <Space>
        {canEditDetailsFields && (
          <Upload
            fileList={files || []}
            showUploadList={false}
            beforeUpload={() => false}
            onChange={(info) => {
              if (onFileChange) {
                onFileChange(fileField, info.fileList);
              }
            }}
            maxCount={1}
            accept=".update,.png,.jpg,.jpeg"
          >
            <Button
              size="small"
              icon={<UploadOutlined />}
              style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
            />
          </Upload>
        )}
        {hasFile && (
          <>
            <Button
              size="small"
              icon={<EyeOutlined />}
              loading={fileActionLoading[fileField]?.view}
              style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
              onClick={() => {
                const file = files?.[0];
                if (file && customerId) {
                  handleView(fileField, file);
                }
              }}
            />
            {canEditDetailsFields && (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                onClick={() => {
                  if (onFileChange) {
                    onFileChange(fileField, []);
                  }
                }}
              />
            )}
          </>
        )}
        {hasRequestedFile && (
          <Button
            size="small"
            icon={<EyeOutlined style={{ color: '#faad14' }} />}
            style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
            onClick={() => {
              handleViewRequested(fileField);
            }}
          />
        )}
      </Space>
    );
  };

  const renderFieldRow = (
    label: string,
    fieldName: string,
    type: 'text' | 'select' | 'textarea' = 'text',
    options?: { label: string; value: string }[],
    showFileUpload?: boolean,
    fileField?: string
  ) => {
    const currentValue = getCurrentValue(fieldName);
    const pendingValue = getPendingValue(fieldName);
    const oldValue = getOldValue(fieldName);
    const hasPending = hasPendingChange(fieldName);
    const changeRequest = getChangeRequestForField(fieldName);
    const requestedValue = getRequestedValue(fieldName);
    const normalizedCurrentValue = normalizeValue(currentValue === '-' ? '' : currentValue);
    const normalizedRequestedValue = normalizeValue(requestedValue);
    const hasRequestedValue = normalizedRequestedValue !== ''
      && normalizedRequestedValue !== normalizedCurrentValue;

    return (
      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
        <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
          <strong>{label}</strong>
        </td>
        <td style={{ padding: '12px', verticalAlign: 'top' }}>
          {canEditDetailsFields ? (
            <div>
              <Form.Item name={fieldName} style={{ margin: 0 }}>
                {type === 'text' && <Input />}
                {type === 'textarea' && <Input.TextArea rows={3} />}
                {type === 'select' && options && (
                  <Select allowClear options={options} placeholder="Select" />
                )}
              </Form.Item>
              {hasRequestedValue && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
                  Requested: {requestedValue}
                </div>
              )}
            </div>
          ) : (
            <div>
              {hasPending && oldValue && pendingValue ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ textDecoration: 'line-through', color: '#999' }}>{oldValue}</span>
                  <span style={{ color: '#faad14', fontWeight: 500 }}>{pendingValue}</span>
                </div>
              ) : (
                <div>{currentValue}</div>
              )}
              {hasRequestedValue && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
                  Requested: {requestedValue}
                </div>
              )}
            </div>
          )}
        </td>
          <td style={{ padding: '12px', width: '200px', verticalAlign: 'top' }}>
          {showFileUpload && fileField ? (
            renderFileUploadButtons(fileField)
          ) : isEdit && hasPending ? (
            <Space>
              {isCustomerPortal ? (
                <Tag color="orange">Pending</Tag>
              ) : canEditDetailsFields ? (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    // Save button - this will be handled by form submission
                    // For now, we can approve the change request
                    if (changeRequest) {
                      handleApproveChangeRequest(changeRequest.id);
                    } else {
                      handleApprovePendingChange(fieldName);
                    }
                  }}
                >
                  Save
                </Button>
              ) : (
                <Button
                  danger
                  size="small"
                  onClick={() => {
                    if (changeRequest) {
                      handleRejectChangeRequest(changeRequest.id);
                    } else {
                      handleRejectPendingChange(fieldName);
                    }
                  }}
                >
                  Cancel
                </Button>
              )}
            </Space>
          ) : null}
          </td>
      </tr>
    );
};

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {renderFieldRow('Name', 'name', 'text', undefined, false)}
          {renderFieldRow('Identification No.', 'ic_passport_no', 'text', undefined, true, 'identification_passport')}
          {renderFieldRow('Nationality', 'nationality', 'text', undefined, false)}
          {renderFieldRow('Sex', 'sex', 'select', sexOptions, false)}
          {renderFieldRow('Race', 'race', 'text', undefined, false)}
          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
              <strong>Assessment</strong>
            </td>
            <td style={{ padding: '12px', verticalAlign: 'top' }}>
              {canEditDetailsFields ? (
                <div>
                  <Form.Item name="assessment" style={{ margin: 0 }}>
                    <Select
                      placeholder="Select Assessment"
                      options={assessmentOptions}
                      onChange={(value) => isCustomerPortal && handleFieldChange('assessment', value)}
                    />
                  </Form.Item>
                  {(() => {
                    const requestedValue = getRequestedValue('assessment');
                    const normalizedRequestedValue = normalizeValue(requestedValue);
                    const normalizedCurrentValue = normalizeValue(form.getFieldValue('assessment') || '');
                    if (normalizedRequestedValue && normalizedRequestedValue !== normalizedCurrentValue) {
                      return (
                        <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
                          Requested: {requestedValue}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              ) : (
                <div>
                  {(() => {
                    const currentAssessment = form.getFieldValue('assessment') || '-';
                    const assessmentChangeRequest = getChangeRequestForField('assessment');
                    const hasAssessmentPending = assessmentChangeRequest && assessmentChangeRequest.status === 'pending';
                    const oldAssessment = assessmentChangeRequest?.old_value;
                    const newAssessment = assessmentChangeRequest?.new_value;
                    
                    if (hasAssessmentPending && oldAssessment && newAssessment) {
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ textDecoration: 'line-through', color: '#999' }}>{oldAssessment}</span>
                          <span style={{ color: '#faad14', fontWeight: 500 }}>{newAssessment}</span>
                        </div>
                      );
                    }
                    return <div>{currentAssessment}</div>;
                  })()}
                  {(() => {
                    const requestedValue = getRequestedValue('assessment');
                    const normalizedRequestedValue = normalizeValue(requestedValue);
                    const normalizedCurrentValue = normalizeValue(form.getFieldValue('assessment') || '');
                    if (normalizedRequestedValue && normalizedRequestedValue !== normalizedCurrentValue) {
                      return (
                        <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
                          Requested: {requestedValue}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </td>
            <td style={{ padding: '12px', width: '200px', verticalAlign: 'top' }}>
              {isEdit && canEditDetailsFields && (() => {
                const assessmentChangeRequest = getChangeRequestForField('assessment');
                const hasAssessmentPending = assessmentChangeRequest && assessmentChangeRequest.status === 'pending';
                return hasAssessmentPending ? (
                  <Space>
                    {isCustomerPortal ? (
                      <Tag color="orange">Pending</Tag>
                    ) : (
                      <>
                        <Button
                          type="primary"
                          size="small"
                          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                          onClick={() => {
                            if (assessmentChangeRequest) {
                              handleApproveChangeRequest(assessmentChangeRequest.id);
                            }
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          danger
                          size="small"
                          onClick={() => {
                            if (assessmentChangeRequest) {
                              handleRejectChangeRequest(assessmentChangeRequest.id);
                            }
                          }}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </Space>
                ) : null;
              })()}
            </td>
          </tr>
          {renderFieldRow('Tin No.', 'tin_no', 'text', undefined, false)}
          {renderFieldRow('Contact No.', 'contact_number', 'text', undefined, false)}
          {renderFieldRow('Email', 'email', 'text', undefined, false)}
          {renderFieldRow('Address', 'address1', 'textarea', undefined, true, 'address')}
          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
              <strong>Bank</strong>
            </td>
            <td style={{ padding: '12px', verticalAlign: 'top' }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  {canEditDetailsFields ? (
                    <div>
                      <Form.Item name={['reporting', 'bank']} style={{ margin: 0 }}>
                        <Input 
                          placeholder="Enter Bank Name"
                          onChange={(e) => isCustomerPortal && handleFieldChange('bank', e.target.value)}
                        />
                      </Form.Item>
                      {(() => {
                        const requestedValue = getRequestedValue('bank');
                        const normalizedRequestedValue = normalizeValue(requestedValue);
                        const normalizedCurrentValue = normalizeValue(form.getFieldValue(['reporting', 'bank']) || '');
                        if (normalizedRequestedValue && normalizedRequestedValue !== normalizedCurrentValue) {
                          return (
                            <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
                              Requested: {requestedValue}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  ) : (
                    <div>
                      {(() => {
                        const reporting = form.getFieldValue('reporting');
                        const currentBank = reporting?.bank || '-';
                        const bankChangeRequest = getChangeRequestForField('bank');
                        const hasBankPending = bankChangeRequest && bankChangeRequest.status === 'pending';
                        const oldBank = bankChangeRequest?.old_value;
                        const newBank = bankChangeRequest?.new_value;
                        
                        if (hasBankPending && oldBank && newBank) {
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ textDecoration: 'line-through', color: '#999' }}>{oldBank}</span>
                              <span style={{ color: '#faad14', fontWeight: 500 }}>{newBank}</span>
                            </div>
                          );
                        }
                        return <div>{currentBank}</div>;
                      })()}
                      {(() => {
                        const requestedValue = getRequestedValue('bank');
                        const normalizedRequestedValue = normalizeValue(requestedValue);
                        const normalizedCurrentValue = normalizeValue(form.getFieldValue('reporting')?.bank || '');
                        if (normalizedRequestedValue && normalizedRequestedValue !== normalizedCurrentValue) {
                          return (
                            <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
                              Requested: {requestedValue}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  {canEditDetailsFields ? (
                    <div>
                      <Form.Item name="bank_number" style={{ margin: 0 }}>
                        <Input placeholder="Enter Account Number" />
                      </Form.Item>
                      {(() => {
                        const requestedValue = getRequestedValue('bank_number');
                        const normalizedRequestedValue = normalizeValue(requestedValue);
                        const normalizedCurrentValue = normalizeValue(form.getFieldValue('bank_number') || '');
                        if (normalizedRequestedValue && normalizedRequestedValue !== normalizedCurrentValue) {
                          return (
                            <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
                              Requested: {requestedValue}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  ) : (
                    <div>
                      <div>{getCurrentValue('bank_number')}</div>
                      {(() => {
                        const requestedValue = getRequestedValue('bank_number');
                        const normalizedRequestedValue = normalizeValue(requestedValue);
                        const normalizedCurrentValue = normalizeValue(form.getFieldValue('bank_number') || '');
                        if (normalizedRequestedValue && normalizedRequestedValue !== normalizedCurrentValue) {
                          return (
                            <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
                              Requested: {requestedValue}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </td>
            <td style={{ padding: '12px', width: '200px', verticalAlign: 'top' }}>
              {renderFileUploadButtons('bank')}
            </td>
            {isEdit && canEditDetailsFields && (() => {
              const bankChangeRequest = getChangeRequestForField('bank');
              const hasBankPending = bankChangeRequest && bankChangeRequest.status === 'pending';
              return hasBankPending ? (
                <td style={{ padding: '12px', width: '200px', verticalAlign: 'top' }}>
                  <Space>
                    {isCustomerPortal ? (
                      <Tag color="orange">Pending</Tag>
                    ) : (
                      <>
                        <Button
                          type="primary"
                          size="small"
                          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                          onClick={() => {
                            if (bankChangeRequest) {
                              handleApproveChangeRequest(bankChangeRequest.id);
                            }
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          danger
                          size="small"
                          onClick={() => {
                            if (bankChangeRequest) {
                              handleRejectChangeRequest(bankChangeRequest.id);
                            }
                          }}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </Space>
                </td>
              ) : null;
            })()}
          </tr>
          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
              <strong>Mytax Password</strong>
            </td>
            <td style={{ padding: '12px', verticalAlign: 'top' }}>
              {canEditDetailsFields ? (
                <div>
                  <Form.Item name="lhdn_password" style={{ margin: 0 }}>
                    <Input />
                  </Form.Item>
                  {(() => {
                    const requestedValue = getRequestedValue('lhdn_password');
                    const normalizedRequestedValue = normalizeValue(requestedValue);
                    const normalizedCurrentValue = normalizeValue(form.getFieldValue('lhdn_password') || '');
                    if (normalizedRequestedValue && normalizedRequestedValue !== normalizedCurrentValue) {
                      return (
                        <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
                          Requested: {requestedValue}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              ) : (
                <div>
                  <div>{getCurrentValue('lhdn_password')}</div>
                  {(() => {
                    const requestedValue = getRequestedValue('lhdn_password');
                    const normalizedRequestedValue = normalizeValue(requestedValue);
                    const normalizedCurrentValue = normalizeValue(form.getFieldValue('lhdn_password') || '');
                    if (normalizedRequestedValue && normalizedRequestedValue !== normalizedCurrentValue) {
                      return (
                        <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
                          Requested: {requestedValue}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </td>
            <td style={{ padding: '12px', width: '200px', verticalAlign: 'top' }}>
              {/* {canEditDetailsFields && (
                <Button
                  size="small"
                  onClick={() => {
                    form.setFieldsValue({ lhdn_password: '' });
                    if (isCustomerPortal) {
                      handleFieldChange('lhdn_password', '');
                    }
                  }}
                >
                  Reset Password
                </Button>
              )} */}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Signature Section */}
      {canViewSignature && (
        <div style={{ marginTop: 24 }}>
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <strong>Signature</strong>
            {canEditSignatureFields && (
              <Space>
                <Upload
                  fileList={reportingFiles.signature || []}
                  showUploadList={false}
                  onChange={(info) => {
                    if (onFileChange) {
                      onFileChange('signature', info.fileList);
                    }
                  }}
                  beforeUpload={() => false}
                  maxCount={1}
                  accept="image/png,image/jpeg,image/jpg"
                >
                  <Button size="small" icon={<UploadOutlined />}>
                    Upload Photo
                  </Button>
                </Upload>
                {hasSignature && canDeleteSignatureField && (
                  <Popconfirm
                    title="Delete signature?"
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => onFileChange && onFileChange('signature', [])}
                  >
                    <Button size="small" danger>
                      Delete
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            )}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ border: '1px solid #d9d9d9', padding: '20px', minHeight: '150px', width: '100%', maxWidth: '500px', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {reportingFiles.signature?.[0] ? (
                <div style={{ width: '100%', textAlign: 'center' }}>
                  {signaturePreviewUrl ? (
                    <img
                      src={signaturePreviewUrl}
                      alt="Signature"
                      style={{ maxWidth: '100%', maxHeight: '200px', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const file = reportingFiles.signature?.[0];
                        if (file && customerId) {
                          handleView('signature', file);
                        }
                      }}
                      onError={() => {
                        // If blob URL fails, try to reload
                        setSignaturePreviewUrl(null);
                      }}
                    />
                  ) : (
                    <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                      Loading preview...
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                  {canEditSignatureFields ? 'No signature uploaded' : 'No signature'}
                </div>
              )}
            </div>
            {!isEditing && getRequestedFileValue('signature') && (
              <div style={{ border: '1px solid #fadb14', padding: '20px', minHeight: '150px', width: '100%', maxWidth: '500px', backgroundColor: '#fffbe6', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 8, left: 12, fontSize: 12, color: '#d4a106', fontWeight: 600 }}>
                  Requested
                </div>
                {requestSignaturePreviewUrl ? (
                  <img
                    src={requestSignaturePreviewUrl}
                    alt="Signature (Request)"
                    style={{ maxWidth: '100%', maxHeight: '200px', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewRequested('signature');
                    }}
                  />
                ) : (
                  <div style={{ color: '#d4a106', textAlign: 'center', padding: '20px' }}>
                    Loading request preview...
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
